import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAsOf } from '../lib/as-of'
import { formatDate } from '../lib/format'
import { useSession } from '../lib/session'
import { ErrorBoundary } from './error-boundary'
import { Button, cx } from './ui'
import { Brand, Icon } from './brand'

/**
 * Every section says what it is, and then what it does.
 *
 * "Band outliers" is precise, and it is also three words of compensation
 * jargon on the first screen somebody sees. A label has to survive being read
 * by a person who has never run a pay review: the name is plain, and the line
 * under it says what the screen answers, so nobody has to click a section to
 * find out whether it is the one they want.
 *
 * The descriptions are hidden on the narrow layout, where the sidebar becomes
 * a horizontal strip and a second line of text would cost more than it
 * explains.
 */
const NAV = [
  { to: '/', label: 'Dashboard', note: 'What everyone is paid', icon: 'dashboard', end: true },
  { to: '/people', label: 'People', note: 'Find and open anyone', icon: 'people' },
  { to: '/bands', label: 'Pay ranges', note: 'Outside their range', icon: 'bands' },
  { to: '/equity', label: 'Pay equity', note: 'Compare like with like', icon: 'equity' },
  { to: '/simulate', label: 'Pay planning', note: 'Cost a raise or offer', icon: 'round' },
  {
    to: '/import',
    label: 'Import',
    note: 'Changes from a file',
    icon: 'import',
    adminOnly: true,
  },
  {
    to: '/audit',
    label: 'Activity',
    note: 'Who changed what',
    icon: 'audit',
    adminOnly: true,
  },
] as const

export function Layout() {
  const { user, signOut, isAdmin } = useSession()
  const { pathname } = useLocation()
  const current = NAV.find((item) => item.to === pathname)?.label ?? 'Employee profile'

  return (
    <div className="workspace-shell">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <aside className="workspace-sidebar">
        <Brand compact />
        <div className="workspace-identity">
          <span className="workspace-monogram">A</span>
          <div>
            Acme Corporation<small>Compensation workspace</small>
          </div>
        </div>
        <nav aria-label="Sections" className="workspace-nav">
          {NAV.filter((item) => !('adminOnly' in item) || isAdmin).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item && item.end}
              className={({ isActive }) =>
                cx(
                  'workspace-nav-link',
                  isActive && 'is-active',
                  item.to === '/import' && 'nav-group-start',
                )
              }
            >
              <Icon name={item.icon} />
              <span className="workspace-nav-text">
                {item.label}
                <small>{item.note}</small>
              </span>
            </NavLink>
          ))}
        </nav>
        <div className="workspace-profile">
          <span className="person-avatar">{user?.name.slice(0, 1)}</span>
          <div>
            <strong>{user?.name}</strong>
            <small>{isAdmin ? 'Administrator' : 'Read only'}</small>
          </div>
        </div>
      </aside>
      <div className="workspace-body">
        <header className="workspace-topbar">
          <div className="workspace-breadcrumb">
            <span>Workspace</span>
            <span aria-hidden="true">/</span>
            <strong>{current}</strong>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <AsOfControl />
            <Button size="sm" variant="ghost" aria-label="Sign out" onClick={signOut}>
              <Icon name="logout" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </header>
        <HistoricalBanner />
        <main id="main-content" tabIndex={-1} className="workspace-main">
          <ErrorBoundary label="the current screen">
            <Outlet />
          </ErrorBoundary>
        </main>
        <footer className="workspace-footer">
          <span>Acme compensation workspace</span>
          <span>Demonstration data. 10,000 generated employees across nine countries.</span>
        </footer>
      </div>
    </div>
  )
}

/**
 * The control that makes the whole product what it is.
 *
 * It sits in the header rather than on a screen because it is not a filter on
 * one report — it changes the date every screen is answering about. Move it to
 * last January and the roster, the payroll total, the band judgements and the
 * equity findings all answer for last January.
 */
function AsOfControl() {
  const { asOf, today, setAsOf, isHistorical, reportToday } = useAsOf()
  // The shell can be entered on any route. Reuse the dashboard query to get
  // the server's current date without trusting the device clock.
  const currentOverview = useQuery({
    queryKey: ['overview', undefined],
    queryFn: () => api.overview({}),
    enabled: !today,
  })
  useEffect(() => {
    if (currentOverview.data) reportToday(currentOverview.data.asOf)
  }, [currentOverview.data, reportToday])

  return (
    <div className="flex items-center gap-1.5">
      <label
        className="hidden text-xs font-medium whitespace-nowrap text-ink-faint sm:inline"
        htmlFor="as-of"
      >
        Showing
      </label>
      <input
        id="as-of"
        type="date"
        aria-label="Show the organisation as it stood on this date. Change it to see any point in its history"
        value={asOf ?? today ?? ''}
        max={today}
        onChange={(event) => setAsOf(event.target.value || undefined)}
        className={cx(
          // 16px on a phone so iOS does not zoom the page when this is
          // tapped, and tall enough to be tapped accurately in the first
          // place. This is the control the whole product turns on.
          'tabular min-h-10 rounded-md border px-2 text-base',
          isHistorical ? 'border-high bg-high-soft text-high' : 'border-line bg-surface',
        )}
      />
      {isHistorical ? (
        <Button size="sm" variant="ghost" onClick={() => setAsOf(undefined)}>
          Today
        </Button>
      ) : null}
    </div>
  )
}

/**
 * Impossible to miss, and that is the point.
 *
 * Every figure on screen is historical while this is showing. Someone who
 * changes the date, walks away, and comes back to a payroll total eight hundred
 * million dollars lower needs to be told why before they email anybody about it.
 */
function HistoricalBanner() {
  const { asOf, isHistorical } = useAsOf()
  if (!isHistorical || !asOf) return null

  return (
    <div className="border-t border-high/20 bg-high-soft px-4 py-1.5 text-center text-xs text-high">
      Showing the organisation as it stood on <strong>{formatDate(asOf)}</strong>. Salaries,
      headcount and band judgements are all as at that date.
    </div>
  )
}
